import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { X, MagnifyingGlass } from '@phosphor-icons/react'
import { Input } from '@/components/ui/Input'
import { useScrollLock } from '@/lib/useScrollLock'
import { useDialogChrome } from '@/pages/sender-emails/AddSenderEmailModal'
import type { AssignableMember } from '@/lib/api'

interface Props {
  senderLabel: string
  senderEmailAddress: string
  /** Resolved assigned members, in the order they were granted. */
  members: AssignableMember[]
  roleLabel: (role: string) => string
  onRevoke: (userId: number) => void
  onClose: () => void
}

/** Rows revealed per scroll-triggered page. Purely a render-window over the
 * already-in-memory `members` array — there is no paginated endpoint behind
 * this (a mailbox's grant list is account-sized, never server-paged), so
 * "infinite scroll" here means "don't mount 200 rows up front," not "fetch
 * more." */
const PAGE_SIZE = 30

/**
 * Read-only(-ish) picker: lists everyone currently granted access to one
 * sender mailbox, with a search box and a per-row revoke — the modal
 * `EmailAccountsSection`'s "N people with access" trigger opens once there's
 * at least one grant (see AB#22366 follow-up). Granting stays on the Access
 * `Select` in the row itself; this is deliberately revoke-only so the two
 * surfaces don't fight over which is "the" way to add someone.
 */
const ManageAccessModal: React.FC<Props> = ({
  senderLabel, senderEmailAddress, members, roleLabel, onRevoke, onClose,
}) => {
  useScrollLock(true)

  const surfaceRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const sentinelRef = useRef<HTMLLIElement>(null)
  const uid = useId()
  const titleId = `${uid}-title`
  const descId = `${uid}-desc`

  useDialogChrome(surfaceRef, onClose, searchRef)

  const [search, setSearch] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return members
    return members.filter((m) => {
      const name = (m.full_name || '').toLowerCase()
      const email = (m.email || '').toLowerCase()
      return name.includes(q) || email.includes(q)
    })
  }, [members, search])

  // A new search re-windows from the top rather than keeping whatever page
  // depth the previous query happened to be scrolled to.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [search])

  const visible = filtered.slice(0, visibleCount)
  const hasMore = visibleCount < filtered.length

  useEffect(() => {
    if (!hasMore) return
    const sentinel = sentinelRef.current
    const root = listRef.current
    if (!sentinel || !root) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => c + PAGE_SIZE)
        }
      },
      { root, rootMargin: '80px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
      {/* Backdrop — blur-only, no dark overlay, matches the rest of the
          sender-email dialog pair. Revoking is a single reversible click
          (re-add from the Access picker), so an outside click closing the
          modal outright — no discard guard — is the right amount of caution. */}
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="relative z-10 w-full max-w-sm rounded-2xl outline-none bg-card border border-border/60 shadow-2xl dark:bg-[#191a1b] dark:border-white/[0.08]"
        initial={{ scale: 0.96, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 8 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-border/50 dark:border-white/[0.06]">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="text-foreground dark:text-[#f7f8f8] truncate"
              style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.15px' }}
            >
              Mailbox access
            </h2>
            <p id={descId} className="mt-0.5 truncate text-[12px] text-muted-foreground dark:text-[#8a8f98]">
              {senderLabel || senderEmailAddress}
              {senderLabel ? ` · ${senderEmailAddress}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-7 w-7 -mr-1 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150 flex-shrink-0"
          >
            <X className="h-4 w-4" weight="bold" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4">
          <Input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
            iconPosition="left"
            aria-label="Search people with access"
            className="h-9"
          />
        </div>

        {/* List */}
        <div className="px-5 pt-3 pb-1">
          <p className="text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#62666d]">
            {/* A single interpolated string, not adjacent JSX expressions —
                the latter renders as several sibling text nodes, which
                breaks exact-string queries (incl. testing-library's
                getByText) even though it reads identically on screen. */}
            {`${filtered.length} ${filtered.length === 1 ? 'person' : 'people'}${
              search ? ` match${filtered.length === 1 ? 'es' : ''}` : ' with access'
            }`}
          </p>
        </div>
        <ul
          ref={listRef}
          role="list"
          aria-label="People with access"
          className="max-h-72 overflow-y-auto px-2 pb-2"
        >
          {visible.length === 0 ? (
            <li className="px-3 py-8 text-center text-[12.5px] text-muted-foreground dark:text-[#8a8f98]">
              {members.length === 0
                ? 'No one has been granted access yet.'
                : `No matches for "${search.trim()}".`}
            </li>
          ) : (
            <>
              {visible.map((m) => {
                const name = m.full_name || m.email
                return (
                  <li
                    key={m.id}
                    className="flex items-center justify-between gap-3 mx-1 px-2.5 py-2 rounded-lg hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors duration-100"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510 }}>
                        {name}
                      </p>
                      <p className="truncate text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11.5px' }}>
                        {m.full_name ? `${m.email} · ${roleLabel(m.role)}` : roleLabel(m.role)}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label={`Revoke access for ${name}`}
                      onClick={() => onRevoke(m.id)}
                      className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded-full text-muted-foreground hover:text-red-600 hover:bg-red-500/[0.08] dark:text-[#62666d] dark:hover:text-red-400 dark:hover:bg-red-500/[0.10] transition-colors duration-150"
                    >
                      <X className="h-3.5 w-3.5" weight="bold" />
                    </button>
                  </li>
                )
              })}
              {/* Reveal-more sentinel — invisible, height-only so
                  IntersectionObserver has something to watch. Rendered only
                  while more rows remain, so scrolling to a fully-loaded
                  list's end never re-triggers the observer. */}
              {hasMore && <li ref={sentinelRef} aria-hidden="true" className="h-px" />}
            </>
          )}
        </ul>
      </motion.div>
    </div>,
    document.body,
  )
}

export default ManageAccessModal
